import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import bg1 from "./../assets/resources/Corridor_view2.jpg";
import bg2 from "./../assets/resources/Lobby_view1.jpg";
import fp1 from "./../assets/resources/floor_plans/floor1.png";
import fp3 from "./../assets/resources/floor_plans/parking.png";
import fp2 from "./../assets/resources/floor_plans/6_11_floor.png";
import bf from "./../assets/resources/Restroom_view1.jpg";
import Carousel from "react-bootstrap/Carousel";
import "./building_page.css";
import SEO from "../Components/SEO/SEO";
import { placeSchema } from "../Components/SEO/structuredData";

export default function Building() {
  const [isScrolledToSection, setIsScrolledToSection] = useState({
    section1: false,
    section2: false,
    section3: false,
    section4: false,
    section5: false,
  });

  const [heroImageLoaded, setHeroImageLoaded] = useState(false);

  const section1Ref = useRef(null);
  const section2Ref = useRef(null);
  const section3Ref = useRef(null);
  const section4Ref = useRef(null);
  const section5Ref = useRef(null);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const id = entry.target.getAttribute("id");
          if (entry.isIntersecting) {
            switch (id) {
              case "section1":
                setIsScrolledToSection({
                  section5: false,
                  section1: true,
                  section2: false,
                  section3: false,
                  section4: false,
                });
                break;
              case "section2":
                setIsScrolledToSection({
                  section5: false,
                  section1: true,
                  section2: true,
                  section3: false,
                  section4: false,
                });
                break;
              case "section3":
                setIsScrolledToSection({
                  section5: false,
                  section1: true,
                  section2: true,
                  section3: true,
                  section4: false,
                });
                break;
              case "section4":
                setIsScrolledToSection({
                  section5: false,
                  section1: true,
                  section2: true,
                  section3: true,
                  section4: true,
                });
                break;
              case "section5":
                setIsScrolledToSection({
                  section5: true,
                  section1: true,
                  section2: true,
                  section3: true,
                  section4: true,
                });
                break;
              default:
                return;
            }
          }
        });
      },
      {
        root: null,
        rootMargin: "0px",
        threshold: 0.1,
      }
    );

    const sections = [
      section1Ref,
      section2Ref,
      section3Ref,
      section4Ref,
      section5Ref,
    ];
    sections.forEach((sectionRef) => {
      if (sectionRef.current) {
        observer.observe(sectionRef.current);
      }
    });

    return () => {
      sections.forEach((sectionRef) => {
        if (sectionRef.current) {
          observer.unobserve(sectionRef.current);
        }
      });
    };
  }, [isScrolledToSection]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <>
      <SEO
        title="The Building | Union Center - Flushing, NY"
        description="Explore Union Center's cutting-edge features including Schüco glass facade, efficient HVAC systems, clean air filtration, and sustainable design. 133,000 square feet of premium office space."
        keywords="Union Center features, sustainable office building, commercial real estate amenities, Flushing office space, Schüco glass facade, HVAC systems, Class A office building"
        canonical="https://www.3716union.com/building"
        ogTitle="The Building | Union Center - Flushing, NY"
        ogDescription="Explore Union Center's cutting-edge features including Schüco glass facade, efficient HVAC systems, clean air filtration, and sustainable design."
        ogImage="https://www.3716union.com/img/resources/union_front.jpg"
        ogUrl="https://www.3716union.com/building"
        structuredData={placeSchema}
      />
      <div className="flex flex-col w-full bg-white">
        <div className="relative w-full h-screen md:h-auto md:flex md:justify-center md:items-center bg-gray-100">
          {/* Loading placeholder */}
          {!heroImageLoaded && (
            <div className="absolute inset-0 bg-gradient-to-br from-gray-200 to-gray-300 animate-pulse flex items-center justify-center"></div>
          )}

          <img
            className={`object-cover w-full h-full md:h-auto md:w-full md:object-contain transition-opacity duration-500 ${
              heroImageLoaded ? "opacity-100" : "opacity-0"
            }`}
            src="/img/resources/union_front.webp"
            alt="Union Center building exterior showcasing modern architectural design"
            loading="eager"
            fetchpriority="high"
            decoding="sync"
            onLoad={() => setHeroImageLoaded(true)}
            onError={() => setHeroImageLoaded(true)} // Fallback to show container even if image fails
          />
        </div>
        <div className="w-full text-[#00000] text-base py-24  bg-white">
          <section className="w-full md:flex">
            <div ref={section1Ref} id="section1"></div>
            <div className="relative justify-center w-full ease-linear opacity-100 pointer-events-auto transform-none">
              <img
                src={bg1}
                alt="Union Center corridor view showcasing modern office design"
                title="The Building"
                className={`w-full max-h-[500px] object-cover ${
                  isScrolledToSection.section1
                    ? "top-0 opacity-100"
                    : "top-[270px] opacity-0"
                } transition-all duration-[800ms] relative`}
              />
            </div>
            <div className="relative flex w-full px-4 md:px-16">
              <div
                className={`max-w-[557px] text-lg relative my-auto ${
                  isScrolledToSection.section1
                    ? "top-0 opacity-100"
                    : "top-[270px] opacity-0"
                } transition-all duration-[800ms]`}
              >
                <p className="pt-10 text-brown md:pt-0">THE BUILDING</p>
                <h2 className="max-w-[557px]  text-[#7e6140] text-4xl font-normal leading-snug md:leading-snug md:text-5xl pb-3">
                  Highly Efficient,
                  <br />
                  Highly Sustainable
                </h2>
                <p>
                  Union Center&apos;s architectural and mechanical systems were
                  sustainably designed to maximize efficiency, wellness, and
                  comfort, while significantly minimizing energy consumption.
                  The building&apos;s unitized Schüco glass facade system adopts
                  modular system components approved by renowned test institutes
                  to optimize lighting, insulation performance, and fresh air
                  circulation.
                </p>
              </div>
            </div>
          </section>

          <section className="w-full mt-10 md:flex md:mt-24">
            <div ref={section2Ref} id="section2"></div>
            <div className="relative justify-center w-full ease-linear pointer-events-auto transform-none">
              <img
                src={bg2}
                alt="Union Center lobby view featuring elegant modern architecture"
                title="The Building"
                className={`w-full  object-cover ${
                  isScrolledToSection.section2
                    ? "top-0 opacity-100"
                    : "top-[270px] opacity-0"
                } transition-all duration-[800ms] relative`}
              />
            </div>
          </section>
          <section className="w-full mt-10 md:flex md:mt-24">
            <div ref={section3Ref} id="section3"></div>
            <div className="w-full pl-[5%] md:pl-[10%] flex relative">
              <div
                className={`max-w-[557px] text-lg relative ${
                  isScrolledToSection.section3
                    ? "top-0 opacity-100"
                    : "top-[270px] opacity-0"
                }  my-auto transition-all duration-[800ms]`}
              >
                <h2 className="max-w-[557px] font-normal text-[#7e6140] text-4xl md:text-5xl pb-3">
                  Building Features
                </h2>
                <ul id="features">
                  <li>Efficient & environmentally friendly </li>
                  <li>Fully electric HVAC system </li>
                  <li>Clean air filtration system </li>
                  <li>Valet parking system </li>
                  <li>Large windows for natural light </li>
                  <li>Thermal insulation & ventilation</li>
                  <li>Easily accessible location </li>
                  <li>Thriving downtown business area </li>
                </ul>
              </div>
            </div>
            <div className="relative justify-center w-full mt-10 ease-linear opacity-100 pointer-events-auto md:mt-0 transform-none">
              <img
                src={bf}
                alt="Union Center restroom facilities showcasing premium amenities"
                title="The Building"
                className={`w-full max-h-[500px] object-cover ${
                  isScrolledToSection.section3
                    ? "top-0 opacity-100"
                    : "top-[270px] opacity-0"
                } transition-all duration-[800ms] relative`}
              />
            </div>
          </section>
        </div>
        <section>
          <div className="w-full flex bg-[#F0EDE8] -mt-4 md:-mt-0">
            <div ref={section4Ref} id="section4"></div>
            <div
              className={`w-full max-w-[880px] mx-auto md:py-[60px] pb-8 pt-10 ${
                isScrolledToSection.section4
                  ? "top-0 opacity-100"
                  : "top-[270px] opacity-0"
              } md:text-center px-4 transition-all duration-[800ms] relative`}
            >
              <p className="text-brown">FLOOR PLANS</p>
              <p className="text-[#7e6140] text-4xl md:text-5xl font-normal leading-snug md:leading-snug pb-1 md:pb-3">
                133,000 Square Feet <br />
                State of the Art Offices
              </p>
              <p>
                Rising 15-stories above street level, the building boasts a
                sleek glass façade, technologically-advanced infrastructure, and
                outdoor space, seamlessly integrating luxury, comfort, and
                functionality.
              </p>
            </div>
          </div>

          <div className="w-full">
            <div ref={section5Ref} id="section5"></div>

            {/* Mobile Carousel */}
            {isMobile ? (
              <div
                className={`${
                  isScrolledToSection.section5
                    ? "top-0 opacity-100"
                    : "top-[270px] opacity-0"
                } transition-all duration-[800ms] relative`}
              >
                <Carousel variant="dark">
                  <Carousel.Item>
                    <img
                      src={fp1}
                      alt="Floor Plan 1"
                      className="w-full h-auto object-contain"
                    />
                  </Carousel.Item>
                  <Carousel.Item>
                    <img
                      src={fp2}
                      alt="Floor Plan 2"
                      className="w-full h-auto object-contain"
                    />
                  </Carousel.Item>
                  <Carousel.Item>
                    <img
                      src={fp3}
                      alt="Floor Plan 2"
                      className="w-full h-auto object-contain"
                    />
                  </Carousel.Item>
                </Carousel>
              </div>
            ) : (
              /* Desktop Side by Side */
              <div
                className={`grid grid-cols-3 gap-4 md:gap-8 max-w-[1400px] mx-auto -mb-20 p-4 md:p-8 ${
                  isScrolledToSection.section5
                    ? "top-0 opacity-100"
                    : "top-[270px] opacity-0"
                } transition-all duration-[800ms] relative`}
              >
                <div className="w-full overflow-hidden">
                  <img
                    src={fp1}
                    alt="Floor Plan 1"
                    className="w-full h-[calc(100%-40px)] object-cover rounded-lg transform translate-y-[-20px]"
                    loading="lazy"
                  />
                </div>
                <div className="w-full overflow-hidden">
                  <img
                    src={fp2}
                    alt="Floor Plan 2"
                    className="w-full h-[calc(100%-40px)] object-cover -mt-6 rounded-lg transform translate-y-[-20px]"
                    loading="lazy"
                  />
                </div>
                <div className="w-full overflow-hidden">
                  <img
                    src={fp3}
                    alt="Floor Plan 2"
                    className="w-full h-[calc(100%-40px)] object-cover rounded-lg transform translate-y-[-20px]"
                    loading="lazy"
                  />
                </div>
              </div>
            )}

            <div className="w-full bg-[#F0EDE8] py-12 px-4  md:px-8">
              <div className="max-w-[1200px] mx-auto text-center">
                <h3 className="text-[#7e6140] text-2xl md:text-3xl font-normal mb-6">
                  View All Floor Plans
                </h3>
                <div className="flex justify-center">
                  <Link
                    to="/availability"
                    className="inline-block bg-[#7d6140ff] text-white px-6 py-3 rounded-full hover:bg-[#000000] transition-colors duration-300"
                  >
                    View Availability
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
